import React from 'react';

/**
 * A row of side-by-side form controls in the folio editor.
 *
 * A plain flex container with a real CSS `gap` rather than a Fluent `<Stack horizontal>`, because Stack
 * implements childrenGap as margins on its children — and every control in these rows carries its own
 * `styles={{root: ...}}` for flexGrow/minWidth, which is a class of equal specificity that can win over
 * the margin Stack injects. The result was rows whose fields sat flush against each other, with the gap
 * silently dropped. `gap` is a property of the container, so nothing a child says can override it.
 *
 * Alignment is by default the top of the controls, NOT the bottom. Bottom alignment lines up the outer
 * boxes, and a field carrying a `description` is taller than one without, so its input is pushed up out
 * of line with its neighbour's — which is the misalignment, not the fix. Aligning at the top puts every
 * label on one line and every input on the next, and lets the descriptions hang below where they belong.
 *
 * `align='controls'` is for rows that end in a bare IconButton: with no label of its own the button would
 * otherwise sit up beside the labels rather than on the line with the inputs it acts on. Only correct
 * where nothing in the row has a description.
 */
export const FieldRow: React.FC<{
    children: React.ReactNode,
    align?: 'labels' | 'controls',
}> = ({ children, align = 'labels' }) => (
    <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '0.75em 1em',
        alignItems: align === 'controls' ? 'flex-end' : 'flex-start',
    }}>
        {children}
    </div>
);

export default FieldRow;
